import axios from "axios";
import { req } from "./api.config";
import { getMonthRanges, mapWithConcurrency } from "../utils/analyticsApiUtils";

const CHUNK_CONCURRENCY = 6;

function mergeLabelStatisticProductionMessages(messages) {
  const byId = new Map();
  let sumCompany = 0;
  let messageId;

  for (const message of messages) {
    if (!message) {
      continue;
    }
    if (message.id !== undefined) {
      messageId = message.id;
    }
    if (message.sum_company !== undefined) {
      sumCompany += Number(message.sum_company) || 0;
    }
    const productions = message.production;
    if (!Array.isArray(productions)) {
      continue;
    }
    for (const prod of productions) {
      const id = Number(prod?.production_id);
      if (!Number.isFinite(id)) {
        continue;
      }
      const addLabels = Number(prod.all_label_prod) || 0;
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, {
          ...prod,
          production_id: id,
          all_label_prod: addLabels,
        });
      } else {
        existing.all_label_prod =
          (Number(existing.all_label_prod) || 0) + addLabels;
        if (prod.name) {
          existing.name = prod.name;
        }
        if (prod.address) {
          existing.address = prod.address;
        }
      }
    }
  }

  return {
    id: messageId,
    sum_company: sumCompany,
    production: Array.from(byId.values()),
  };
}

function isRequestCanceled(e) {
  return e?.code === "ERR_CANCELED" || e?.name === "CanceledError";
}

export class AnalyticsForbiddenError extends Error {
  constructor() {
    super("Forbidden");
    this.isForbidden = true;
    this.name = "AnalyticsForbiddenError";
  }
}

class ApiDataAnalitic {
  formatIncidentDowntime(count) {
    let time = count;
    if (time <= 59) time = `${Math.round(time)} сек.`;
    if (time > 59 && time < 3600) time = `${Math.round(time / 60)} мин.`;
    if (time >= 3600) time = `${Math.round(time / 60 / 24)} час.`;
    return time;
  }

  async fetchLabelStatisticChunk({
    companyId,
    from,
    to,
    step,
    event,
    signal,
  }) {
    try {
      const res = await req(
        `reports/label_statistic_sa?company=${companyId}` +
          `&filterdate=${from}&filterdate=${to}` +
          `&step=${step}&event=${event}`,
        { suppressNoRight: true, signal },
      );
      return res?.data?.message;
    } catch (e) {
      if (isRequestCanceled(e)) {
        throw e;
      }
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        throw new AnalyticsForbiddenError();
      }
      console.error("fetchLabelStatisticChunk error", e);
      return undefined;
    }
  }

  async fetchLabelStatisticSa({
    companyId,
    filterdate: [from, to],
    step,
    event,
    signal,
  }) {
    const ranges =
      step === "mon" || step === "y"
        ? getMonthRanges(from, to)
        : [{ from, to }];

    const results = await mapWithConcurrency(
      ranges,
      CHUNK_CONCURRENCY,
      ({ from: rangeFrom, to: rangeTo }) =>
        this.fetchLabelStatisticChunk({
          companyId,
          from: rangeFrom,
          to: rangeTo,
          step,
          event,
          signal,
        }),
    );

    if (ranges.length === 1) return results[0];

    return mergeLabelStatisticProductionMessages(results);
  }

  mergeLabelStatisticProductionMessages(messages) {
    return mergeLabelStatisticProductionMessages(messages);
  }

  async fetchLabelStatisticSaYearMerged({
    companyId,
    from,
    to,
    event = "p",
    signal,
  }) {
    const ranges = getMonthRanges(from, to);
    const results = await mapWithConcurrency(
      ranges,
      CHUNK_CONCURRENCY,
      ({ from: rangeFrom, to: rangeTo }) =>
        this.fetchLabelStatisticChunk({
          companyId,
          from: rangeFrom,
          to: rangeTo,
          step: "d",
          event,
          signal,
        }),
    );
    if (ranges.length === 1) {
      return results[0];
    }
    return mergeLabelStatisticProductionMessages(results);
  }

  async fetchIncidentSa({
    companyId,
    from,
    to,
    fields_name,
    data,
    signal,
  }) {
    let url =
      `reports/incident_sa?company=${companyId}` +
      `&filterdate=${from}&filterdate=${to}`;
    if (data !== undefined && fields_name !== undefined) {
      url += `&data=${encodeURIComponent(data)}&fields_name=${fields_name}`;
    }
    try {
      const res = await req(url, { suppressNoRight: true, signal });
      return res?.data?.message;
    } catch (e) {
      if (isRequestCanceled(e)) {
        throw e;
      }
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        throw new AnalyticsForbiddenError();
      }
      console.error("fetchIncidentSa error", e);
      return undefined;
    }
  }
}

export default new ApiDataAnalitic();
